import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { FileTextIcon, CheckIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ChevronRightIcon, FilterIcon, SearchIcon, CalendarIcon, EyeIcon, DownloadIcon, MessageSquareIcon, MoreHorizontalIcon, XIcon } from 'lucide-react';
interface StudentApplicationsProps {
  isTabbed?: boolean;
}
// Sample application data
const applicationData = [{
  id: 'APP001',
  program: 'Master of Business Administration',
  university: 'UAE Business School',
  location: 'Dubai, UAE',
  status: 'submitted',
  submissionDate: '2023-05-15',
  lastUpdated: '2023-05-20',
  decisionDate: '2023-07-01',
  priority: 'high',
  progress: 100,
  notes: 'Your application is currently under review by the admissions committee.',
  track: 'Regular Admission',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Personal Statement',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'APP002',
  program: 'PhD in Computer Science',
  university: 'Emirates Technical University',
  location: 'Abu Dhabi, UAE',
  status: 'in_progress',
  submissionDate: null,
  lastUpdated: '2023-05-18',
  decisionDate: null,
  priority: 'medium',
  progress: 65,
  notes: 'Please complete your research proposal and upload your recommendation letters.',
  track: 'Graduate Research',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Research Proposal',
    status: 'missing'
  }, {
    name: 'Recommendation Letters',
    status: 'pending'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'APP003',
  program: 'Bachelor of Science in Data Science',
  university: 'National University of Science',
  location: 'Sharjah, UAE',
  status: 'under_review',
  submissionDate: '2023-04-30',
  lastUpdated: '2023-05-10',
  decisionDate: '2023-06-15',
  priority: 'high',
  progress: 100,
  notes: 'Your application is being reviewed by the department faculty.',
  track: 'Elite Track',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Personal Statement',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'APP004',
  program: 'Master of Education',
  university: 'Future Teachers College',
  location: 'Dubai, UAE',
  status: 'approved',
  submissionDate: '2023-03-15',
  lastUpdated: '2023-04-20',
  decisionDate: '2023-04-20',
  priority: 'medium',
  progress: 100,
  notes: 'Congratulations! Your application has been approved. Please check your email for enrollment instructions.',
  track: 'Regular Admission',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Personal Statement',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}, {
  id: 'APP005',
  program: 'Bachelor of Arts in International Relations',
  university: 'Global Affairs University',
  location: 'Abu Dhabi, UAE',
  status: 'rejected',
  submissionDate: '2023-03-01',
  lastUpdated: '2023-04-15',
  decisionDate: '2023-04-15',
  priority: 'low',
  progress: 100,
  notes: 'We regret to inform you that your application was not successful. You may apply again in the next intake period.',
  track: 'Regular Admission',
  documents: [{
    name: 'Academic Transcript',
    status: 'verified'
  }, {
    name: 'Personal Statement',
    status: 'verified'
  }, {
    name: 'Recommendation Letters',
    status: 'verified'
  }, {
    name: 'CV/Resume',
    status: 'verified'
  }]
}];
const StudentApplications: React.FC<StudentApplicationsProps> = ({
  isTabbed = false
}) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Filter applications based on status and search query
  const filteredApplications = applicationData.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesSearch = searchQuery === '' || app.program.toLowerCase().includes(searchQuery.toLowerCase()) || app.university.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  // Format date string
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Get status display information
  const getStatusInfo = status => {
    switch (status) {
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-3575" />
        };
      case 'submitted':
        return {
          label: 'Submitted',
          color: 'bg-gray-100 text-gray-700',
          icon: <CheckIcon className="w-4 h-4 mr-1.5" data-id="element-3576" />
        };
      case 'under_review':
        return {
          label: 'Under Review',
          color: 'bg-gray-100 text-gray-700',
          icon: <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-3577" />
        };
      case 'approved':
        return {
          label: 'Approved',
          color: 'bg-green-100 text-green-700',
          icon: <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3578" />
        };
      case 'rejected':
        return {
          label: 'Rejected',
          color: 'bg-red-100 text-red-700',
          icon: <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3579" />
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-700',
          icon: <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3580" />
        };
    }
  };
  // Get track badge style
  const getTrackBadge = track => {
    if (track === 'Elite Track') {
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    } else if (track === 'Graduate Research') {
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    } else {
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };
  // Get priority badge style
  const getPriorityBadge = priority => {
    switch (priority) {
      case 'high':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      case 'medium':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      case 'low':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };
  // Get action button based on status
  const getActionButton = application => {
    switch (application.status) {
      case 'in_progress':
        return {
          label: 'Continue Application',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'submitted':
      case 'under_review':
        return {
          label: 'Track Status',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'approved':
        return {
          label: 'Proceed to Enrollment',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
      case 'rejected':
        return {
          label: 'View Feedback',
          color: 'bg-gray-700 hover:bg-gray-800 text-white'
        };
      default:
        return {
          label: 'View Details',
          color: 'bg-primary-500 hover:bg-primary-600 text-white'
        };
    }
  };
  // Handle view details
  const handleViewDetails = application => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  };
  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedApplication(null);
  };
  const content = <div className="space-y-6" data-id="element-3581">
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-3582">
        <div className="flex flex-col md:flex-row gap-4 justify-between" data-id="element-3583">
          <div className="relative w-full md:w-64" data-id="element-3584">
            <input type="text" placeholder="Search applications..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3585" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-3586" />
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-3587">
            <div className="flex items-center space-x-2" data-id="element-3588">
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('all')} data-id="element-3589">
                All
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'in_progress' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('in_progress')} data-id="element-3590">
                In Progress
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'submitted' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('submitted')} data-id="element-3591">
                Submitted
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'under_review' ? 'bg-gray-200 text-gray-800' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('under_review')} data-id="element-3592">
                Under Review
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'approved' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('approved')} data-id="element-3593">
                Approved
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${statusFilter === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`} onClick={() => setStatusFilter('rejected')} data-id="element-3594">
                Rejected
              </button>
            </div>
            <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-3595">
              <FilterIcon className="h-4 w-4 mr-1.5" data-id="element-3596" />
              More Filters
            </button>
          </div>
        </div>
      </div>
      {/* Applications List */}
      {filteredApplications.length > 0 ? <div className="space-y-4" data-id="element-3597">
          {filteredApplications.map(application => {
        const statusInfo = getStatusInfo(application.status);
        const actionButton = getActionButton(application);
        return <div key={application.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 transition-all" data-id="element-3598">
                <div className="p-5" data-id="element-3599">
                  <div className="flex flex-col md:flex-row justify-between md:items-start gap-4" data-id="element-3600">
                    {/* Application Information */}
                    <div className="flex-grow" data-id="element-3601">
                      <div className="flex justify-between items-start" data-id="element-3602">
                        <div data-id="element-3603">
                          <h3 className="text-lg font-medium text-gray-800" data-id="element-3604">
                            {application.program}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1" data-id="element-3605">
                            {application.university}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-3606">
                            {application.location}
                          </p>
                        </div>
                        <div className="flex-shrink-0" data-id="element-3607">
                          <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${statusInfo.color}`} data-id="element-3608">
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      {/* Application Timeline */}
                      {application.status !== 'in_progress' && <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-id="element-3609">
                          <div data-id="element-3610">
                            <p className="text-xs text-gray-500" data-id="element-3611">
                              Submitted On
                            </p>
                            <p className="text-gray-700" data-id="element-3612">
                              {formatDate(application.submissionDate)}
                            </p>
                          </div>
                          <div data-id="element-3613">
                            <p className="text-xs text-gray-500" data-id="element-3614">
                              Last Updated
                            </p>
                            <p className="text-gray-700" data-id="element-3615">
                              {formatDate(application.lastUpdated)}
                            </p>
                          </div>
                          {application.decisionDate && <div data-id="element-3616">
                              <p className="text-xs text-gray-500" data-id="element-3617">
                                Decision Date
                              </p>
                              <p className="text-gray-700" data-id="element-3618">
                                {formatDate(application.decisionDate)}
                              </p>
                            </div>}
                        </div>}
                      {/* Progress bar for in-progress applications */}
                      {application.status === 'in_progress' && <div className="mt-4" data-id="element-3619">
                          <div className="flex items-center justify-between mb-1" data-id="element-3620">
                            <p className="text-sm text-gray-600" data-id="element-3621">
                              Application Progress
                            </p>
                            <p className="text-sm font-medium text-gray-700" data-id="element-3622">
                              {application.progress}%
                            </p>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-3623">
                            <div className="bg-primary-500 h-2.5 rounded-full" style={{
                      width: `${application.progress}%`
                    }} data-id="element-3624"></div>
                          </div>
                          <p className="mt-2 text-sm text-gray-600" data-id="element-3625">
                            {application.notes}
                          </p>
                        </div>}
                      {/* Badges */}
                      <div className="mt-4 flex flex-wrap gap-2" data-id="element-3626">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${getTrackBadge(application.track)}`} data-id="element-3627">
                          {application.track}
                        </span>
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${getPriorityBadge(application.priority)}`} data-id="element-3628">
                          {application.priority.charAt(0).toUpperCase() + application.priority.slice(1)}{' '}
                          Priority
                        </span>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 md:items-end" data-id="element-3629">
                      <button className={`px-4 py-2 rounded-md text-sm font-medium ${actionButton.color}`} data-id="element-3630">
                        {actionButton.label}
                      </button>
                      <button onClick={() => handleViewDetails(application)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-3631">
                        <EyeIcon className="w-4 h-4 mr-1.5" data-id="element-3632" />
                        View Details
                      </button>
                      {/* Additional actions for submitted applications */}
                      {(application.status === 'submitted' || application.status === 'under_review') && <div className="flex gap-2 mt-2" data-id="element-3633">
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-3634">
                            <MessageSquareIcon className="w-4 h-4" data-id="element-3635" />
                          </button>
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-3636">
                            <DownloadIcon className="w-4 h-4" data-id="element-3637" />
                          </button>
                          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-3638">
                            <MoreHorizontalIcon className="w-4 h-4" data-id="element-3639" />
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>;
      })}
        </div> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center" data-id="element-3640">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-3641">
            <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-3642" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2" data-id="element-3643">
            No applications found
          </h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto mb-5" data-id="element-3644">
            {searchQuery ? 'No applications match your search criteria. Try adjusting your filters or search terms.' : "You haven't started any applications yet. Explore programs and apply to ones that match your interests."}
          </p>
          <button className="h-10 px-4 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center" data-id="element-3645">
            <SearchIcon className="w-4 h-4 mr-2" data-id="element-3646" />
            Explore Programs
          </button>
        </div>}
      {/* Application Details Modal */}
      {showDetailModal && selectedApplication && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3647">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3648">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal} data-id="element-3649"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-3650">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-3651">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3652">
                <div className="flex justify-between items-start mb-4" data-id="element-3653">
                  <div data-id="element-3654">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3655">
                      Application Details
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-3656">
                      {selectedApplication.program} at{' '}
                      {selectedApplication.university}
                    </p>
                  </div>
                  <button onClick={closeDetailModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-3657">
                    <XIcon className="h-6 w-6" data-id="element-3658" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3659">
                  {/* Application Information */}
                  <div data-id="element-3660">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-3661">
                      Application Information
                    </h4>
                    <div className="space-y-3" data-id="element-3662">
                      <div data-id="element-3663">
                        <span className="text-sm text-gray-500" data-id="element-3664">
                          Application ID:
                        </span>
                        <p className="text-sm text-gray-900 font-medium" data-id="element-3665">
                          {selectedApplication.id}
                        </p>
                      </div>
                      <div data-id="element-3666">
                        <span className="text-sm text-gray-500" data-id="element-3667">Status:</span>
                        <p className="text-sm" data-id="element-3668">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusInfo(selectedApplication.status).color}`} data-id="element-3669">
                            {getStatusInfo(selectedApplication.status).label}
                          </span>
                        </p>
                      </div>
                      <div data-id="element-3670">
                        <span className="text-sm text-gray-500" data-id="element-3671">Track:</span>
                        <p className="text-sm" data-id="element-3672">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getTrackBadge(selectedApplication.track)}`} data-id="element-3673">
                            {selectedApplication.track}
                          </span>
                        </p>
                      </div>
                      <div data-id="element-3674">
                        <span className="text-sm text-gray-500" data-id="element-3675">Priority:</span>
                        <p className="text-sm" data-id="element-3676">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getPriorityBadge(selectedApplication.priority)}`} data-id="element-3677">
                            {selectedApplication.priority.charAt(0).toUpperCase() + selectedApplication.priority.slice(1)}{' '}
                            Priority
                          </span>
                        </p>
                      </div>
                      {selectedApplication.submissionDate && <div data-id="element-3678">
                          <span className="text-sm text-gray-500" data-id="element-3679">
                            Submitted On:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3680">
                            {formatDate(selectedApplication.submissionDate)}
                          </p>
                        </div>}
                      <div data-id="element-3681">
                        <span className="text-sm text-gray-500" data-id="element-3682">
                          Last Updated:
                        </span>
                        <p className="text-sm text-gray-900" data-id="element-3683">
                          {formatDate(selectedApplication.lastUpdated)}
                        </p>
                      </div>
                      {selectedApplication.decisionDate && <div data-id="element-3684">
                          <span className="text-sm text-gray-500" data-id="element-3685">
                            Decision Date:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3686">
                            {formatDate(selectedApplication.decisionDate)}
                          </p>
                        </div>}
                    </div>
                  </div>
                  {/* Documents */}
                  <div data-id="element-3687">
                    <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-3688">
                      Application Documents
                    </h4>
                    <div className="space-y-2" data-id="element-3689">
                      {selectedApplication.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md" data-id="element-3690">
                          <div className="flex items-center" data-id="element-3691">
                            <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-3692" />
                            <span className="text-sm text-gray-700" data-id="element-3693">
                              {doc.name}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${doc.status === 'verified' ? 'bg-gray-100 text-gray-700' : doc.status === 'pending' ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-3694">
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
                {/* Notes */}
                <div className="mt-6" data-id="element-3695">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-3696">
                    Notes
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3" data-id="element-3697">
                    <p className="text-sm text-gray-700" data-id="element-3698">
                      {selectedApplication.notes}
                    </p>
                  </div>
                </div>
                {/* Timeline */}
                <div className="mt-6" data-id="element-3699">
                  <h4 className="font-medium text-sm text-gray-500 uppercase tracking-wider mb-3" data-id="element-3700">
                    Application Timeline
                  </h4>
                  <div className="space-y-6" data-id="element-3701">
                    <div className="relative" data-id="element-3702">
                      <div className="flex items-center" data-id="element-3703">
                        <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-3704">
                          <CalendarIcon className="h-5 w-5 text-gray-600" data-id="element-3705" />
                        </div>
                        <div className="ml-4" data-id="element-3706">
                          <h3 className="text-sm font-medium text-gray-900" data-id="element-3707">
                            Application Started
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-3708">
                            {formatDate(new Date(new Date(selectedApplication.lastUpdated).getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-3709"></div>
                    </div>
                    {selectedApplication.submissionDate && <div className="relative" data-id="element-3710">
                        <div className="flex items-center" data-id="element-3711">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-3712">
                            <CheckIcon className="h-5 w-5 text-gray-600" data-id="element-3713" />
                          </div>
                          <div className="ml-4" data-id="element-3714">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-3715">
                              Application Submitted
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-3716">
                              {formatDate(selectedApplication.submissionDate)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-3717"></div>
                      </div>}
                    {selectedApplication.status === 'under_review' && <div className="relative" data-id="element-3718">
                        <div className="flex items-center" data-id="element-3719">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center z-10" data-id="element-3720">
                            <ClockIcon className="h-5 w-5 text-gray-600" data-id="element-3721" />
                          </div>
                          <div className="ml-4" data-id="element-3722">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-3723">
                              Under Review
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-3724">
                              {formatDate(selectedApplication.lastUpdated)}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 left-4 -ml-0.5 h-full w-0.5 bg-gray-200 z-0" data-id="element-3725"></div>
                      </div>}
                    {(selectedApplication.status === 'approved' || selectedApplication.status === 'rejected') && <div className="relative" data-id="element-3726">
                        <div className="flex items-center" data-id="element-3727">
                          <div className={`h-9 w-9 rounded-full ${selectedApplication.status === 'approved' ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center z-10`} data-id="element-3728">
                            {selectedApplication.status === 'approved' ? <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-3729" /> : <XCircleIcon className="h-5 w-5 text-red-600" data-id="element-3730" />}
                          </div>
                          <div className="ml-4" data-id="element-3731">
                            <h3 className="text-sm font-medium text-gray-900" data-id="element-3732">
                              Application{' '}
                              {selectedApplication.status === 'approved' ? 'Approved' : 'Rejected'}
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-3733">
                              {formatDate(selectedApplication.decisionDate)}
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3734">
                <button className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${getActionButton(selectedApplication).color}`} data-id="element-3735">
                  {getActionButton(selectedApplication).label}
                </button>
                <button onClick={closeDetailModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3736">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
  return <>
      {!isTabbed ? <Layout title="My Applications" persona="Student" data-id="element-3737">
          {content}
        </Layout> : content}
    </>;
};
export default StudentApplications;